import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type Server, type SshKey, type Tenant } from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge, statusTone } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Pencil } from 'lucide-react';
import { formatDate, relativeTime } from '@/lib/utils';
import { ServerMetricsPanel } from '@/components/ServerMetricsPanel';

export function ServerDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const detail = useQuery({
    queryKey: ['server', id],
    queryFn: () => api.get<{ server: Server; tenants: Tenant[] }>(`/api/servers/${id}`),
    // Background poller writes fresh metrics every 30s — match that cadence
    // so an operator leaving the page open sees live data without manual
    // clicks. Pause when the tab is hidden.
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  const healthCheck = useMutation({
    mutationFn: () => api.post(`/api/servers/${id}/health-check`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['server', id] }),
  });

  const updateStatus = useMutation({
    mutationFn: (status: Server['status']) => api.patch(`/api/servers/${id}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['server', id] }),
  });

  const [editOpen, setEditOpen] = useState(false);

  if (detail.isLoading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (detail.isError || !detail.data) return <div className="text-sm text-red-600">Server not found.</div>;

  const { server, tenants } = detail.data;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/servers" className="text-xs text-slate-500 hover:text-slate-700">
          ← Back to servers
        </Link>
        <div className="flex items-center gap-3 mt-2">
          <h1 className="text-2xl font-semibold text-slate-900 font-mono">{server.id}</h1>
          <Badge tone={statusTone(server.status)}>{server.status}</Badge>
          {server.health_status && <Badge tone={statusTone(server.health_status)}>{server.health_status}</Badge>}
        </div>
        <p className="text-sm text-slate-500 mt-1 font-mono">{server.hostname}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Identity</CardTitle>
          </CardHeader>
          <CardBody className="text-sm space-y-2">
            <Row label="Region" value={server.region} />
            <Row label="SSH user" value={server.ssh_user} />
            <Row label="Public IP" value={server.public_ip || '— (learned on the first cross-server move)'} />
            <Row label="Capacity" value={server.capacity.toString()} />
            <Row label="Created" value={formatDate(server.created_at)} />
            <Row label="Last health" value={server.last_health_at ? `${formatDate(server.last_health_at)} (${relativeTime(server.last_health_at)})` : '—'} />
            <Row label="Notes" value={server.notes || '—'} />
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Actions</CardTitle>
          </CardHeader>
          <CardBody className="space-y-2">
            <Button variant="outline" className="w-full" onClick={() => setEditOpen(true)}>
              <Pencil size={14} /> Edit server
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => healthCheck.mutate()}
              disabled={healthCheck.isPending}
            >
              {healthCheck.isPending ? 'Checking…' : 'Run health check'}
            </Button>
            {server.status === 'active' && (
              <Button variant="outline" className="w-full" onClick={() => updateStatus.mutate('draining')}>
                Mark draining
              </Button>
            )}
            {server.status === 'draining' && (
              <Button variant="outline" className="w-full" onClick={() => updateStatus.mutate('active')}>
                Mark active
              </Button>
            )}
          </CardBody>
        </Card>
      </div>

      <EditServerDialog server={server} open={editOpen} onOpenChange={setEditOpen} />

      <ServerMetricsPanel server={server} />

      <Card>
        <CardHeader>
          <CardTitle>Tenants on this server ({tenants.length})</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {tenants.length === 0 ? (
            <div className="p-5 text-sm text-slate-500">No tenants yet.</div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  <th className="px-5 py-3">ID</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3">OAuth</th>
                  <th className="px-3 py-3">Customer</th>
                  <th className="px-3 py-3">Created</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => (
                  <tr key={t.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-5 py-3 font-mono text-xs">
                      <Link to={`/tenants/${t.id}`} className="text-slate-900 hover:text-slate-700">
                        {t.id}
                      </Link>
                    </td>
                    <td className="px-3 py-3">
                      <Badge tone={statusTone(t.status)}>{t.status}</Badge>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col gap-1">
                        <Badge tone={statusTone(t.claude_oauth_status)}>claude: {t.claude_oauth_status}</Badge>
                        <Badge tone={statusTone(t.codex_oauth_status)}>codex: {t.codex_oauth_status}</Badge>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-slate-700">{t.customer_email ?? '—'}</td>
                    <td className="px-3 py-3 text-xs text-slate-500">{relativeTime(t.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function EditServerDialog({
  server,
  open,
  onOpenChange,
}: {
  server: Server;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const sshKeys = useQuery({
    queryKey: ['ssh-keys'],
    queryFn: () => api.get<{ ssh_keys: SshKey[] }>('/api/ssh-keys'),
    enabled: open,
  });
  const [form, setForm] = useState({
    capacity: server.capacity,
    ssh_user: server.ssh_user,
    ssh_key_id: server.ssh_key_id ?? '',
    hostname: server.hostname,
    public_ip: server.public_ip ?? '',
    notes: server.notes ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/api/servers/${server.id}`, {
        capacity: form.capacity,
        ssh_user: form.ssh_user,
        ssh_key_id: form.ssh_key_id || null,
        hostname: form.hostname,
        public_ip: form.public_ip.trim() || null,
        notes: form.notes || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['server', server.id] });
      qc.invalidateQueries({ queryKey: ['servers'] });
      onOpenChange(false);
      setError(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  const usedRatio = (server.tenant_count ?? 0) / server.capacity;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Edit ${server.id}`}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error && <div className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</div>}
        <div>
          <Label htmlFor="hostname">Hostname</Label>
          <Input
            id="hostname"
            value={form.hostname}
            onChange={(e) => setForm({ ...form, hostname: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="ssh_user">SSH user</Label>
            <Input
              id="ssh_user"
              value={form.ssh_user}
              onChange={(e) => setForm({ ...form, ssh_user: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="capacity">Capacity</Label>
            <Input
              id="capacity"
              type="number"
              min={1}
              value={form.capacity}
              onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value, 10) || 1 })}
            />
            <p className="text-xs text-slate-500 mt-1">
              {server.tenant_count ?? 0} tenant{server.tenant_count === 1 ? '' : 's'} placed
              {usedRatio > 0.8 ? ' — near capacity' : ''}
            </p>
          </div>
        </div>
        <div>
          <Label htmlFor="public_ip">Public IP</Label>
          <Input
            id="public_ip"
            value={form.public_ip}
            onChange={(e) => setForm({ ...form, public_ip: e.target.value })}
            placeholder="learned from the host on the first cross-server move"
          />
          <p className="text-xs text-slate-500 mt-1">
            Only used when a website is moved between servers: the other host is told to accept the
            transfer from this address. Correct it here if this server sits behind NAT.
          </p>
        </div>
        <div>
          <Label htmlFor="ssh_key_id">SSH key</Label>
          <select
            id="ssh_key_id"
            className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
            value={form.ssh_key_id}
            onChange={(e) => setForm({ ...form, ssh_key_id: e.target.value })}
          >
            <option value="">— (none)</option>
            {sshKeys.data?.ssh_keys.map((k) => (
              <option key={k.id} value={k.id}>
                {k.label} ({k.public_fingerprint.slice(0, 24)}…)
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>
      </div>
    </Dialog>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-slate-500 text-xs uppercase tracking-wide">{label}</span>
      <span className="text-slate-900 font-mono text-xs text-right break-all">{value}</span>
    </div>
  );
}
